import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router";
import Track from '../../../assets/track.svg'
import TrackActive from '../../../assets/trackActive.svg'
import Trash from '../../../assets/trash.svg'
import TrashActive from '../../../assets/trashActive.svg'
import { FormattedMessage } from "react-intl";
import { FormValues } from "./types";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { Modal } from "ui-kit/Modal";
import { Button } from "ui-kit/Button";

interface AudioFile {
  file: File;
  url: string;
  id: string;
}

interface SummaryProps {
  setAudioFiles: React.Dispatch<React.SetStateAction<AudioFile[]>>;
  coverUrl?: string,
  tracksData?: { id: string; title: string; has_audio: boolean }[],
  setLoading?: (loading: boolean) => void,
  setTracksData?: (tracksData: { id: string; title: string; has_audio: boolean }[]) => void,
  setAdd: (add: boolean) => void
  add: boolean
  editId: any,
  setEditId: (editId: any) => void
  release: FormValues
  onSubmit:any
  values:any
  onAddTrack:any
  orderedTracks:any
  setOrderedTracks: (orderedTracks: any) => void
  disableAddTrack?: boolean
  deleteTrack: (trackId: string) => Promise<void>;
  refreshTracks: () => Promise<any>;
  reorderReleaseTracks: (releaseId: number, data: any) => Promise<any>;
  listLoading?: boolean;

}


export const Summary: React.FC<SummaryProps> = ({ setAudioFiles, coverUrl, tracksData, setAdd, editId, setEditId, release, onSubmit, values,  onAddTrack, setOrderedTracks, orderedTracks, disableAddTrack, deleteTrack, refreshTracks, reorderReleaseTracks, listLoading }) => {

  const [deletingIds, setDeletingIds] = useState<Record<string, boolean>>({});
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeTo, setUpgradeTo] = useState<"EP" | "ALBUM" | null>(null);
  const pendingUpgradeActionRef = useRef<null | (() => void)>(null);

  const HARD_MAX_TRACKS = 12;
  const tracksCount =
    (Array.isArray(orderedTracks) ? orderedTracks.length : 0) ||
    (Array.isArray(tracksData) ? tracksData.length : 0);

  // Release type limits (from server only):
  // Single = 1–2 tracks, EP = 3–6, Album = 7–12
  // Backend ids in this app: Album=1, Single=2, EP=3.
  const { maxTracksAllowed, releaseTypeLabel } = (() => {
    const typeId = (release as any)?.release_type_id;
    const type = String((release as any)?.release_type ?? "").toLowerCase();

    if (String(typeId) === "2" || type === "single") {
      return { maxTracksAllowed: 2, releaseTypeLabel: "Single" };
    }
    if (String(typeId) === "3" || type === "ep") {
      return { maxTracksAllowed: 6, releaseTypeLabel: "EP" };
    }
    if (String(typeId) === "1" || type === "album") {
      return { maxTracksAllowed: 12, releaseTypeLabel: "Album" };
    }
    // Fallback: treat as Album if type is unknown.
    return { maxTracksAllowed: 12, releaseTypeLabel: null as string | null };
  })();

  const hideAddTrack = tracksCount >= HARD_MAX_TRACKS;
  const isAtHardLimit = tracksCount >= HARD_MAX_TRACKS;

  const capacityLabel = releaseTypeLabel
    ? `${tracksCount}/${maxTracksAllowed} ${releaseTypeLabel} now`
    : `${tracksCount}/${maxTracksAllowed}`;

  const releaseTitle =
    (release as any)?.title ??
    (release as any)?.releaseTitle ??
    "—";

  const hasReleaseTitle =
    String(releaseTitle ?? "").trim() !== "" && String(releaseTitle) !== "—";
  const hasCover = !!String(coverUrl ?? "").trim();

  const openUpgradeModalIfNeeded = (onOk: () => void) => {
    if (tracksCount >= maxTracksAllowed && maxTracksAllowed < HARD_MAX_TRACKS) {
      setUpgradeTo(maxTracksAllowed === 2 ? "EP" : "ALBUM");
      pendingUpgradeActionRef.current = onOk;
      setIsUpgradeModalOpen(true);
      return true;
    }
    return false;
  };
  const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const { id } = useParams();
  const navigate = useNavigate();

  const handleRedirect = (trackId: string) => {
    const finalLink = `/releases/new-release/${id}?step=track-upload`;
    setEditId(trackId)
    navigate(finalLink);
  };

  const onDeleteTrack = async (trackId: string) => {
    if (String(trackId) === "new") {
      // Local-only draft row
      setOrderedTracks((prev: any[]) => (prev || []).filter(t => String(t?.id) !== "new"));
      setEditId(tracksData?.[0]?.id);
      setAdd(false);
      setAudioFiles([]);
      return;
    }
    setDeletingIds((prev) => ({ ...prev, [String(trackId)]: true }));
    // Optimistically remove from UI
    setOrderedTracks((prev: any[]) => (Array.isArray(prev) ? prev.filter(t => String(t?.id) !== String(trackId)) : prev));
    try {
    await deleteTrack(trackId);
      await refreshTracks();
    } catch (e) {
      console.error("[Summary] deleteTrack failed", e);
      // Restore list from server
      await refreshTracks();
    } finally {
      setDeletingIds((prev) => {
        const next = { ...prev };
        delete next[String(trackId)];
        return next;
      });
    }
    // setOrderedTracks((prev:any) =>
    //   prev.filter((track: any) => track.id !== trackId)
    // );
  };
  

  useEffect(() => {
    !editId && setEditId(tracksData?.[0]?.id)
  }, [tracksData])

  const visibleTracks =
    Array.isArray(orderedTracks) ? orderedTracks.filter(Boolean) : [];
  const hasSingleDraftOnly =
    visibleTracks.length === 1 && String(visibleTracks[0]?.id) === "new";


  return (
    <Container>
      {listLoading && (
        <SummaryLoadingOverlay>
          <DeletingSpinner aria-label="Loading" />
        </SummaryLoadingOverlay>
      )}
      <Modal
        visible={isUpgradeModalOpen}
        onHide={() => {
          setIsUpgradeModalOpen(false);
          setUpgradeTo(null);
          pendingUpgradeActionRef.current = null;
        }}
        dismissableMask
        showHeader={false}
        footer={(
          <ModalFooter>
            <Button
              onClick={() => {
                const fn = pendingUpgradeActionRef.current;
                setIsUpgradeModalOpen(false);
                setUpgradeTo(null);
                pendingUpgradeActionRef.current = null;
                fn?.();
              }}
            >
              OK
            </Button>
          </ModalFooter>
        )}
      >
        <ModalBody>
          {upgradeTo === "EP" ? (
            <div>
              Adding another track will change your release type to <b>EP</b>.
            </div>
          ) : (
            <div>
              Adding another track will change your release type to <b>Album</b>.
            </div>
          )}
        </ModalBody>
      </Modal>
  
      <Box>
        <Cover>
          {hasCover ? (
            <PreviewImage src={String(coverUrl)} alt="Cover art" />
          ) : (
            <CoverPlaceholder>
              <CoverSpinner aria-label="Loading cover" />
            </CoverPlaceholder>
          )}
        </Cover>
      </Box>
      <NameBox>
        {hasReleaseTitle ? (
        <ReleaseName title={String(releaseTitle)}>
          {String(releaseTitle)}
        </ReleaseName>
        ) : (
          <ReleaseNamePlaceholder>
            <SmallSpinner aria-label="Loading title" />
          </ReleaseNamePlaceholder>
        )}
      </NameBox>
      <DragDropContext
        onDragEnd={async (result: DropResult) => {
          if (!result.destination) return;

          const newTracks = reorder(
            orderedTracks || [],
            result.source.index,
            result.destination.index
          );

          setOrderedTracks(newTracks);

          try {
            // Don't send draft ("new") row to backend reorder endpoint.
            const orderedIds = newTracks.map((t) => Number(t.id)).filter((n) => Number.isFinite(n));
            await reorderReleaseTracks(Number(release?.id), { order: orderedIds });
          } catch (err) {
            console.error("Reorder error:", err);
          }
        }}
      >
        <Droppable
          droppableId="tracks-droppable"
          isDropDisabled={false}
          isCombineEnabled={false}
          ignoreContainerClipping={false}
          direction="vertical"
        >
          {(provided) => (
            <TrackList ref={provided.innerRef} {...provided.droppableProps}>
              {visibleTracks.map(
                (track: { id: string; title: string; has_audio: boolean }, index: number) => (
                  <Draggable
                    key={track.id}
                    draggableId={track.id.toString()}
                    index={index}
                  >
                    {(providedDrag) => (
                      <TrackItem
                        ref={providedDrag.innerRef}
                        {...providedDrag.draggableProps}
                        {...providedDrag.dragHandleProps}
                        onClick={() => {
                          if (String(track.id) === "new") {
                            setEditId("new");
                            setAdd(true);
                            return;
                          }
                          handleRedirect(track.id);
                          setAdd(false);
                        }}
                        style={{
                          backgroundColor:
                            String(editId) === String(track.id) ? "#E2E5FF" : undefined,
                          ...providedDrag.draggableProps.style,
                        }}
                      >
                        <TrackBox>
                          {String(editId) === String(track.id) ? <TrackActive /> : <Track />}
                          {index + 1}
                          <div>
                            {track.title}
                            {track.has_audio && (
                              <div>
                                <FormattedMessage
                                  id="NewRelease.TrackUploadInfo.AudioAvailable"
                                  defaultMessage="Audio available"
                                />
                              </div>
                            )}
                          </div>
                        </TrackBox>
                        {!(hasSingleDraftOnly && String(track.id) === "new") && (
                          <DeleteButton
                            type="button"
                            onMouseDown={(ev) => {
                              // Prevent drag handle from grabbing this interaction
                              ev.stopPropagation();
                            }}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              onDeleteTrack(track.id);
                            }}
                          >
                            {deletingIds[String(track.id)] ? (
                              <DeletingSpinner aria-label="Deleting" />
                            ) : String(editId) === String(track.id) ? (
                              <TrashActive />
                            ) : (
                              <Trash />
                            )}
                          </DeleteButton>
                        )}
                      </TrackItem>
                    )}
                  </Draggable>
                )
              )}
              {provided.placeholder}

              <>
                <More>
                  <MoreHint>
                    {!isAtHardLimit && "You can add more tracks if you need a different release type."}
                  </MoreHint>
                  <MoreValue>
                    {capacityLabel}
                  </MoreValue>
                </More>
                {!hideAddTrack && (
                  <>
                  <AddTrackButton
                    disabled={!!disableAddTrack || String(editId) === "new"}
                    onClick={() => {
                      if (disableAddTrack) return;
                      // Don't allow opening another "new track" while the current one isn't added to the list yet.
                      if (String(editId) === "new") return;

                      const proceed = () => {
                        // Create a local draft row in the list so user sees it immediately.
                        setOrderedTracks((prev: any[]) => {
                          const list = Array.isArray(prev) ? prev : [];
                          if (list.some(t => String(t?.id) === "new")) return list;
                          return [
                            ...list,
                            { id: "new", title: "New track (draft)", has_audio: false },
                          ];
                        });
                        setEditId("new");
                        setAdd(true);
                        setAudioFiles([]);
                      };

                      if (openUpgradeModalIfNeeded(proceed)) return;

                      proceed();
                    }}
                  >
                    +
                    <FormattedMessage
                      id="NewRelease.TrackUploadInfo.AddTrack"
                      defaultMessage="Add track"
                    />
                  </AddTrackButton>
                  </>
                )}
              </>

            </TrackList>
          )}
        </Droppable>
      </DragDropContext>


    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0 30px;
  width: 334px;
  border-radius: 30px;
  background-color: ${props => props.theme.palette.background.primary};
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
  }
`;

const Title = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #000000;
`

const Cover = styled.div`
  width: 312px;
  height: 300px;
  border-radius: 20px;
  padding-top:12px;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    padding-top: 12px;
    aspect-ratio: 1 / 1;
    overflow: hidden;
  }
`
const PreviewImage = styled.img`
  max-width: 300px;
  height: 300px;
  border-radius: 20px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CoverPlaceholder = styled.div`
  width: 300px;
  height: 300px;
  border-radius: 20px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
  }
`;

const SpinnerBase = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 3px solid rgba(0, 0, 0, 0.12);
  border-top-color: rgba(0, 0, 0, 0.5);
  display: inline-block;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const CoverSpinner = styled(SpinnerBase)``;

const ReleaseNamePlaceholder = styled.div`
  width: 140px;
  height: 20px;
  display: flex;
  align-items: center;
`;

const SmallSpinner = styled(SpinnerBase)`
  width: 16px;
  height: 16px;
  border-width: 2px;
`;

const NameBox = styled.div`
  max-width: 95%;
  height: 50px;
  display: flex;
  flex-direction: column;
  padding: 14px 0 0 17px;

  @media (max-width: 768px) {
  justify-content: center;
  align-items: center;
    width: 100%;
    height: 50px;
    margin: 0px 15px 15px 0px;
  }
`
const ReleaseName = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 18px;
  line-height: 25px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #000000;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const TrackList = styled.div`
  width:100%;
  display:flex; 
  flex-direction: column;
  gap:12px;
  list-style: none;
  padding: 0;
  padding: 15px 7px;
  position: relative;

  @media (max-width: 768px) {
    padding: 15px 12px;
  }
`

const SummaryLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  border-radius: 14px;
`;

const TrackItem = styled.div`
  width:100%;
  padding: 12px;
  display:flex;
  justify-content:space-between;
  align-items: center;
  // border: 1px solid #ccc;
  border-radius: 8px;
  height: 47px;
  border-radius: 10px;
  // background-color:#E2E5FF;
  cursor:pointer;
`

const TrackBox = styled.div`
  display:flex;
  align-items: center;
  gap:7px;
`

const Box = styled.div`
  padding-left:17px ;
  padding-top: 14px;
  height:348px;

  @media (max-width: 768px) {
    padding: 0px;
  }
`

const More = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-family: SF Pro Display;
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  letter-spacing: 0;
  vertical-align: middle;
  color: rgba(0, 0, 0, 0.6);
`;

const MoreHint = styled.span`
  flex: 1;
  text-align: left;
`;

const MoreValue = styled.span`
  white-space: nowrap;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 0 8px;
`;

const ModalBody = styled.div`
  padding: 18px 6px 6px;
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: #000000;
`;

const AddTrackButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  height: 47px;
  border-radius: 10px;
  display:flex;
  justify-content:center;
  align-items: center;
  border: none;
  background-color: ${({ disabled }) => (disabled ? "#00000040" : "#000000")};
  font-family: Outfit;
  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 0;
  text-align: center;
  vertical-align: middle;
  color:#FFFFFF;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
`
const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const DeleteButton = styled.button`
  border: none;
  background: transparent;
  padding: 6px;
  margin: -6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  min-height: 28px;

  /* Make sure SVG doesn't swallow events */
  svg {
    pointer-events: none;
  }
`;

const DeletingSpinner = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(0, 0, 0, 0.18);
  border-top-color: rgba(0, 0, 0, 0.55);
  display: inline-block;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;